import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { MdOutlineWbSunny, MdWifiOff } from "react-icons/md";
import { FaPowerOff } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useConfirmationContext } from "@contexts/ConfirmationContext";
import { updateUser } from "@slices/userSlice";
import ProductService from "@services/ProductService";
import Product, { ProductLog } from "@ltypes/product";
import FlashType from "@ltypes/flash";
import User from "@ltypes/user";
import DateHelper from "@helpers/DateHelper";
import Button from "@components/forms/Button/Button";
import Badges from "@components/ui/Badges/Badges";
import Badge from "@components/ui/Badge/Badge";
import SliderFilled from "@components/forms/SliderFilled/SliderFilled";
import CheckboxCard from "@components/forms/CheckboxCard/CheckboxCard";
import Flash from "@components/ui/Flash/Flash";
import Loader from "@components/ui/Loader/Loader";
import MakeAFlash from "@components/modals/MakeAFlashModal/MakeAFlash";
import Update from "@components/ui/Update/Update";
import Table from "@components/ui/Table/Table";
import ProductForm from "@components/organisms/ProductForm/ProductForm";
import Alert from "@components/ui/Alert/Alert";

type DashboardPageParams = {
    productId: string,
};

function DashboardPage() {
    const params = useParams<DashboardPageParams>();

    const [isLoading, setLoading] = useState<boolean>(true);
    const [lumeba, setLumeba] = useState<Product>();
    const [makeAFlashModalVisible, setMakeAFlashModalVisible] = useState<boolean>(false);
    const [isConnected, setConnected] = useState<boolean>(true);

    // const timerRef = useRef<any>(null);

    const { t } = useTranslation();
    const { openConfirmationModal } = useConfirmationContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user: User|null = useSelector((state: any) => state.user);

    const service = new ProductService;

    const handleChange = (field: string, value: any, updateState: boolean = true) => {
        if(!lumeba)
            return;

        const orignalLumeba = lumeba;

        if(updateState)
            setLumeba({...lumeba, [field]: value});

        service.update(lumeba.id, {
            [field]: value,
        }).then(() => {
        }).catch((e) => {
            toast.error(t('toast.error_lumeba_update'));
            if(updateState)
                setLumeba(orignalLumeba);
        });
    };

    const handleUpdateProduct = (formData: {[key: string]: any}) => {
        if(!lumeba)
            return;

        setLumeba({...lumeba, ...formData});
    };

    const handleStopFlash = () => {
        if(!lumeba)
            return;

        const promise = service.deleteSceneFlash(lumeba.id);
        toast.promise(promise, {
            loading: t('toast.deleting'),
            success: () => t('toast.deleted'),
            error: err => err.body.message || t('toast.error_fetching'),
        });

        promise.then(() => {
            handleUpdateProduct({flash: null});
        });
    };

    const getLogAction = (log: ProductLog): React.ReactNode => {
        let element: React.ReactNode = (<Badge color="gray">{t('text.unknown')}</Badge>);
        if(log.type === 0) {
            element = (<Badge color="primary">{t('text.connected')}</Badge>)
        }
        else if(log.type === 1) {
            element = (<Badge color="red">{t('text.disconnected')}</Badge>)
        }
        else if(log.type === 2) {
            element = (<Badge color="yellow">{t('text.update')} {log.data.version ? `: v${log.data.version}` : ''}</Badge>)
        }

        return element;
    };

    const handleUnpair = () => {
        if(!lumeba)
            return;

        openConfirmationModal(t('page.dashboard.confirmation_unpair'), () => {
            const promise = service.unpair(lumeba.id);
            toast.promise(promise, {
                loading: t('toast.unpairing'),
                success: () => t('toast.unpaired'),
                error: err => err.body.message || t('toast.error_fetching'),
            });
            promise.then(() => {
                if(user) {
                    dispatch(updateUser({products: user.products.filter((p: Product) => p.id !== lumeba.id)}));
                }
                navigate('/');
            });
        }, {
            description: t('page.dashboard.confirmation_unpair_description')
        });
    };

    useEffect(() => {
        if(!params.productId)
            return;

        let connected: boolean|null = null;
        
        service.get(params.productId).then(data => {
            setLumeba(data.body);
            connected = data.body.is_connected;
        }).catch(() => {
            toast.error(t('toast.error_lumeba_not_found'), {
                duration: 10000,
            });
        }).finally(() => {
            setLoading(false);
            if(connected === false) {
                setConnected(false);
            }
        });
    }, [params.productId]);

    // useEffect(() => {
    //     clearInterval(timerRef.current);
    //     if(!lumeba || !lumeba.flash) {
    //         return;
    //     }

    //     timerRef.current = setInterval(() => {
    //         if(!lumeba || !lumeba.flash) {
    //             return;
    //         }
    //         const expiredAt = new Date(lumeba.flash.expire_at);
    //         const now = new Date();

    //         if(now > expiredAt) {
    //             handleUpdateProduct({flash: null});
    //         }
    //     }, 1000);
    // }, [lumeba]);

    return (
        <>
            <div className="p-3" style={{marginBottom: '4rem'}}>
                <h1>{t('title.dashboard')}</h1>

                {isLoading ? (
                    <Loader size="5rem"/>
                ) : !lumeba ? (
                    <>{t('text.error')}</>
                ) : (
                    <>
                        {lumeba.new_version !== null && <Update product={lumeba} version={lumeba.new_version} onUpdate={setLumeba}/>}

                        {!isConnected && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <Alert color="yellow">
                                        <div className="row align-items-center">
                                            <div className="col-auto">
                                                <MdWifiOff size="3rem"/>
                                            </div>
                                            <div className="col">
                                                {t('page.dashboard.offline')}
                                            </div>
                                        </div>
                                    </Alert>
                                </div>
                            </div>
                        )}

                        <div className="row mt-3">
                            <div className="col-12">
                                <h2>{lumeba.name}</h2>
                            </div>
                        </div>

                        <div className="row g-3 mt-1 align-items-center">
                            <div className="col-12 col-md-4">
                                <Badges items={[
                                    {
                                        label: 'Serial number',
                                        value: lumeba.serial_number,
                                    },
                                    {
                                        label: 'Version',
                                        value: lumeba.version.name,
                                    },
                                ]}/>
                            </div>
                            <div className="col-12 col-md-8">
                                <SliderFilled icon={MdOutlineWbSunny} value={lumeba.brightness} disabled={!lumeba.is_connected} onChange={(value: number) => handleChange('brightness', value)}/>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <div className="row g-3 align-items-center">
                                    <div className="col-5 col-md-4">
                                        <div className="d-flex justify-content-evenly">
                                            <CheckboxCard
                                                label="OFF"
                                                labelActive="ON"
                                                icon={FaPowerOff}
                                                active={lumeba.is_screen_on}
                                                disabled={!lumeba.is_connected}
                                                onChange={(checked: boolean) => handleChange('is_screen_on', checked)}/>
                                        </div>
                                    </div>

                                    <div className="col-7 col-md-4">
                                        <Button filled bottomElement={
                                            lumeba.flash ? <Flash flash={lumeba.flash} onStop={handleStopFlash}/> : undefined
                                        } disabled={!lumeba.is_connected} onClick={() => setMakeAFlashModalVisible(true)}>{t(`page.dashboard.input.${lumeba.flash ? 'change_flash' : 'make_flash'}`)}</Button>
                                    </div>

                                    <div className="col-12 col-md-4">
                                        {/* <Button filled className="red" disabled={1 === 1 || !lumeba.is_connected}>{t('page.dashboard.input.reset')}</Button> */}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12 col-md-6">
                                <h2>{t('page.dashboard.parameters')}</h2>

                                <ProductForm product={lumeba} onUpdate={handleUpdateProduct}/>
                                <div className="d-flex justify-content-end mt-3">
                                    <Button className="red" size="sm" onClick={handleUnpair}>{t('page.dashboard.product_unpair')}</Button>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <h2>{t('page.dashboard.logs')}</h2>

                                <Table
                                    className="mt-3"
                                    columns={[t('page.dashboard.date'), t('page.dashboard.action')]}
                                    position={['left', 'left']}
                                    rows={lumeba.latest_logs.map((log: ProductLog) => [
                                        DateHelper.formatDate(log.date, true),
                                        getLogAction(log),
                                    ])}
                                    filled
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            <MakeAFlash product={lumeba} onCreate={(flash: FlashType) => handleUpdateProduct({flash: flash})} visible={makeAFlashModalVisible} onHide={() => setMakeAFlashModalVisible(false)}/>
        </>
    );
}

export default DashboardPage;
